import React, { useState } from "react";
import { Modal, Stack, Group, Button } from "@mantine/core";
import ClientOrPhoneLookup from "../common/ClientOrPhoneLookup";
import { useMessaging } from "../../context/MessagingContext";

interface NewChatModalProps {
  opened: boolean;
  onClose: () => void;
  /** Called with the created (or re-opened) conversation's shareable token. */
  onCreated: (conversationId: string) => void;
}

interface Recipient {
  phoneE164: string;
  clientId?: string;
}

/**
 * Start a new chat: search a client OR type a phone number directly (you are
 * never forced to pick a client). The backend normalizes the number and returns
 * the existing thread if one already exists (idempotent), so this doubles as
 * "jump to a number's conversation".
 */
export const NewChatModal: React.FC<NewChatModalProps> = ({
  opened,
  onClose,
  onCreated,
}) => {
  const { createConversation } = useMessaging();
  const [phoneE164, setPhoneE164] = useState("");
  const [clientId, setClientId] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setPhoneE164("");
    setClientId("");
    setError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleCreate = async () => {
    if (!phoneE164) {
      setError("Choose a client or enter a valid phone number");
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const conversation = await createConversation(
        phoneE164,
        clientId || undefined,
      );
      reset();
      onCreated(conversation.conversation_id);
      onClose();
    } catch {
      setError("Could not start the chat. Check the number and try again.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal opened={opened} onClose={handleClose} title="New chat" centered>
      <Stack gap="md">
        <ClientOrPhoneLookup
          error={error ?? undefined}
          onChange={(recipient: Recipient | null) => {
            setError(null);
            setPhoneE164(recipient?.phoneE164 || "");
            setClientId(recipient?.clientId || "");
          }}
        />

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            color="teal"
            loading={creating}
            disabled={!phoneE164}
            onClick={handleCreate}
          >
            Start chat
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default NewChatModal;
